import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSidebar } from '../../hooks/useSidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import FilterToolbar from './components/FilterToolbar';
import MetricsPanel from './components/MetricsPanel';
import PermitTableRow from './components/PermitTableRow';
import PermitMobileCard from './components/PermitMobileCard';
import BulkActionsBar from './components/BulkActionsBar';
import ExtendPermitModal from './components/ExtendPermitModal';
import ClosePermitModal from './components/ClosePermitModal';

const WorkPermitTracking = () => {
  const navigate = useNavigate();
  const { isCollapsed } = useSidebar();

  const [filters, setFilters] = useState({
    location: 'all',
    status: 'all',
    permitType: 'all',
    search: '',
    startDate: '',
    endDate: ''
  });

  const [sortConfig, setSortConfig] = useState({
    key: 'permitId',
    direction: 'asc'
  });

  const [selectedPermits, setSelectedPermits] = useState([]);
  const [extendModalPermit, setExtendModalPermit] = useState(null);
  const [closeModalPermit, setCloseModalPermit] = useState(null);

  const mockPermits = [
    {
      id: 1,
      permitId: 'WP-2025-001',
      workDescription: 'Welding repairs on main production line conveyor system',
      permitType: 'hot-work',
      location: 'plant-1',
      status: 'active',
      assignedTo: 'John Martinez',
      startDate: '2025-01-15T08:00:00',
      endDate: '2025-01-20T17:00:00'
    },
    {
      id: 2,
      permitId: 'WP-2025-002',
      workDescription: 'Inspection and maintenance of underground storage tank',
      permitType: 'confined-space',
      location: 'plant-2',
      status: 'pending',
      assignedTo: 'Sarah Johnson',
      startDate: '2025-01-18T09:00:00',
      endDate: '2025-01-19T16:00:00'
    },
    {
      id: 3,
      permitId: 'WP-2025-003',
      workDescription: 'Electrical panel upgrade in control room',
      permitType: 'electrical',
      location: 'plant-1',
      status: 'active',
      assignedTo: 'Michael Chen',
      startDate: '2025-01-10T07:00:00',
      endDate: '2025-01-25T18:00:00'
    },
    {
      id: 4,
      permitId: 'WP-2025-004',
      workDescription: 'Roof maintenance and leak repair',
      permitType: 'height-work',
      location: 'warehouse-1',
      status: 'extended',
      assignedTo: 'David Rodriguez',
      startDate: '2025-01-05T08:00:00',
      endDate: '2025-01-22T17:00:00'
    },
    {
      id: 5,
      permitId: 'WP-2024-089',
      workDescription: 'Foundation excavation for new equipment installation',
      permitType: 'excavation',
      location: 'plant-3',
      status: 'expired',
      assignedTo: 'Emily Thompson',
      startDate: '2024-12-20T08:00:00',
      endDate: '2025-01-10T17:00:00'
    },
    {
      id: 6,
      permitId: 'WP-2025-005',
      workDescription: 'General maintenance of HVAC system',
      permitType: 'general',
      location: 'plant-2',
      status: 'active',
      assignedTo: 'Robert Wilson',
      startDate: '2025-01-16T08:00:00',
      endDate: '2025-01-18T17:00:00'
    },
    {
      id: 7,
      permitId: 'WP-2025-006',
      workDescription: 'Cutting and grinding operations for structural modifications',
      permitType: 'hot-work',
      location: 'plant-1',
      status: 'pending',
      assignedTo: 'Lisa Anderson',
      startDate: '2025-01-20T08:00:00',
      endDate: '2025-01-23T17:00:00'
    },
    {
      id: 8,
      permitId: 'WP-2024-095',
      workDescription: 'Completed electrical installation project',
      permitType: 'electrical',
      location: 'warehouse-1',
      status: 'closed',
      assignedTo: 'James Brown',
      startDate: '2024-12-15T08:00:00',
      endDate: '2025-01-05T17:00:00'
    }
  ];

  const filteredPermits = useMemo(() => {
    return mockPermits?.filter(permit => {
      if (filters?.location !== 'all' && permit?.location !== filters?.location) return false;
      if (filters?.status !== 'all' && permit?.status !== filters?.status) return false;
      if (filters?.permitType !== 'all' && permit?.permitType !== filters?.permitType) return false;
      
      if (filters?.search) {
        const searchLower = filters?.search?.toLowerCase();
        if (!permit?.permitId?.toLowerCase()?.includes(searchLower) && 
            !permit?.workDescription?.toLowerCase()?.includes(searchLower)) {
          return false;
        }
      }

      if (filters?.startDate) {
        const permitStart = new Date(permit.startDate);
        const filterStart = new Date(filters.startDate);
        if (permitStart < filterStart) return false;
      }

      if (filters?.endDate) {
        const permitEnd = new Date(permit.endDate);
        const filterEnd = new Date(filters.endDate);
        if (permitEnd > filterEnd) return false;
      }

      return true;
    });
  }, [filters]);

  const sortedPermits = useMemo(() => {
    const sorted = [...filteredPermits];
    sorted?.sort((a, b) => {
      let aValue = a?.[sortConfig?.key];
      let bValue = b?.[sortConfig?.key];

      if (sortConfig?.key === 'startDate' || sortConfig?.key === 'endDate') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredPermits, sortConfig]);

  const metrics = useMemo(() => {
    const now = new Date();
    const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    return {
      activePermits: mockPermits?.filter(p => p?.status === 'active' || p?.status === 'extended')?.length,
      pendingApprovals: mockPermits?.filter(p => p?.status === 'pending')?.length,
      expiringSoon: mockPermits?.filter(p => {
        const endDate = new Date(p.endDate);
        return (p?.status === 'active' || p?.status === 'extended') && endDate <= sevenDaysFromNow && endDate >= now;
      })?.length,
      totalThisMonth: mockPermits?.filter(p => new Date(p.startDate) >= firstDayOfMonth)?.length
    };
  }, []);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      location: 'all',
      status: 'all',
      permitType: 'all',
      search: '',
      startDate: '',
      endDate: ''
    });
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSelectPermit = (id, checked) => {
    setSelectedPermits(prev => 
      checked ? [...prev, id] : prev?.filter(permitId => permitId !== id)
    );
  };

  const handleSelectAll = (checked) => {
    setSelectedPermits(checked ? sortedPermits?.map(p => p?.id) : []);
  };

  const handleViewDetails = (id) => {
    navigate('/work-permit-details', { state: { permitId: id } });
  };

  const handleExtendPermit = (id) => {
    const permit = mockPermits?.find(p => p?.id === id);
    setExtendModalPermit(permit);
  };

  const handleClosePermit = (id) => {
    const permit = mockPermits?.find(p => p?.id === id);
    setCloseModalPermit(permit);
  };

  const handleConfirmExtension = (id, extensionData) => {
    console.log('Extending permit:', id, extensionData);
    setExtendModalPermit(null);
    setSelectedPermits([]);
  };

  const handleConfirmClose = (id, closeReason) => {
    console.log('Closing permit:', id, closeReason);
    setCloseModalPermit(null);
    setSelectedPermits([]);
  };

  const handleBulkExtend = () => {
    if (selectedPermits?.length === 1) {
      handleExtendPermit(selectedPermits?.[0]);
    } else {
      console.log('Bulk extending permits:', selectedPermits);
      setSelectedPermits([]);
    }
  };

  const handleBulkClose = () => {
    if (selectedPermits?.length === 1) {
      handleClosePermit(selectedPermits?.[0]);
    } else {
      console.log('Bulk closing permits:', selectedPermits);
      setSelectedPermits([]);
    }
  };

  const handleBulkExport = () => {
    console.log('Exporting permits:', selectedPermits);
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig?.key !== columnKey) {
      return <Icon name="ChevronsUpDown" size={14} className="text-muted-foreground" />;
    }
    return sortConfig?.direction === 'asc' 
      ? <Icon name="ChevronUp" size={14} className="text-primary" />
      : <Icon name="ChevronDown" size={14} className="text-primary" />;
  };

  return (
    <div className={` bg-background min-h-screen`}>
      <div className="p-6 lg:p-8">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-3xl font-bold text-foreground">Work Permit Tracking</h1>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                iconName="Home"
                onClick={() => navigate('/')}
              >
                Return to Dashboard
              </Button>
              <Button
                variant="default"
                iconName="Plus"
                onClick={() => navigate('/work-permit-creation')}
              >
                Create Permit
              </Button>
            </div>
          </div>
          <p className="text-muted-foreground">
            Monitor and manage all work permits with real-time status updates
          </p>
        </div>

        <MetricsPanel metrics={metrics} />

        <FilterToolbar
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
          resultsCount={sortedPermits?.length}
        />

        <div className="hidden lg:block bg-card border border-border rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="px-4 py-3 text-left w-12">
                    <input
                      type="checkbox"
                      checked={selectedPermits?.length === sortedPermits?.length && sortedPermits?.length > 0}
                      onChange={(e) => handleSelectAll(e?.target?.checked)}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary/20"
                      aria-label="Select all permits"
                    />
                  </th>
                  <th className="px-4 py-3 text-left">
                    <button
                      onClick={() => handleSort('permitId')}
                      className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors"
                    >
                      Permit ID
                      <SortIcon columnKey="permitId" />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <button
                      onClick={() => handleSort('workDescription')}
                      className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors"
                    >
                      Work Description
                      <SortIcon columnKey="workDescription" />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <button
                      onClick={() => handleSort('location')}
                      className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors"
                    >
                      Location
                      <SortIcon columnKey="location" />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <button
                      onClick={() => handleSort('status')}
                      className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors"
                    >
                      Status
                      <SortIcon columnKey="status" />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <button
                      onClick={() => handleSort('assignedTo')}
                      className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors"
                    >
                      Assigned To
                      <SortIcon columnKey="assignedTo" />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <button
                      onClick={() => handleSort('startDate')}
                      className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors"
                    >
                      Dates
                      <SortIcon columnKey="startDate" />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <span className="text-sm font-semibold text-foreground">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedPermits?.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-4 py-12 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <Icon name="Search" size={48} className="text-muted-foreground" />
                        <p className="text-muted-foreground">No permits found matching your filters</p>
                        <Button variant="outline" size="sm" onClick={handleClearFilters}>
                          Clear Filters
                        </Button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  sortedPermits?.map(permit => (
                    <PermitTableRow
                      key={permit?.id}
                      permit={permit}
                      isSelected={selectedPermits?.includes(permit?.id)}
                      onSelect={handleSelectPermit}
                      onViewDetails={handleViewDetails}
                      onExtend={handleExtendPermit}
                      onClose={handleClosePermit}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="lg:hidden space-y-4">
          {sortedPermits?.length === 0 ? (
            <div className="bg-card border border-border rounded-lg p-12 text-center">
              <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground mb-4">No permits found matching your filters</p>
              <Button variant="outline" size="sm" onClick={handleClearFilters}>
                Clear Filters
              </Button>
            </div>
          ) : (
            sortedPermits?.map(permit => (
              <PermitMobileCard
                key={permit?.id}
                permit={permit}
                isSelected={selectedPermits?.includes(permit?.id)}
                onSelect={handleSelectPermit}
                onViewDetails={handleViewDetails}
                onExtend={handleExtendPermit}
                onClose={handleClosePermit}
              />
            ))
          )}
        </div>

        <BulkActionsBar
          selectedCount={selectedPermits?.length}
          onExtendSelected={handleBulkExtend}
          onCloseSelected={handleBulkClose}
          onExportSelected={handleBulkExport}
          onClearSelection={() => setSelectedPermits([])}
        />

        {extendModalPermit && (
          <ExtendPermitModal
            permit={extendModalPermit}
            onClose={() => setExtendModalPermit(null)}
            onConfirm={handleConfirmExtension}
          />
        )}

        {closeModalPermit && (
          <ClosePermitModal
            permit={closeModalPermit}
            onClose={() => setCloseModalPermit(null)}
            onConfirm={handleConfirmClose}
          />
        )}
      </div>
    </div>
  );
};

export default WorkPermitTracking;